import React, { useEffect, useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { fetchStaffMember } from "../../actions/staff/clubStaffActions";
import NotificationMessage from "../../components/notification/NotificationMessage";
import TaskOverview from "../../components/taskoverview/TaskOverview";
import ThemeContext from "../../theme/ThemeContext";
import { useTranslation } from 'react-i18next';
import { formatDate, formatBirth } from '../../components/dateUtils';
import Button from "../../components/cofButton/Button";
import styles from './StaffMemberScreen.module.css';

const StaffMemberScreen = () => {
  const { t } = useTranslation();
  const { staffMemberId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const staffMemberInfo = useSelector((state) => state.staffMember);
  const { loading, error, staffMember } = staffMemberInfo;
  console.log(staffMember);

  const theme = useContext(ThemeContext);
  const { colors, fonts } = theme;

  // State za upravljanje brojem prikazanih notifikacija
  const [visibleNotifications, setVisibleNotifications] = useState(5);

  useEffect(() => {
    dispatch(fetchStaffMember(userInfo.club, staffMemberId));
    // Reset visible notifications when staff member changes
    setVisibleNotifications(5);
  }, [dispatch, staffMemberId, userInfo.club]);

  const handleTaskClick = (taskId) => {
    // Navigacija na željenu rutu
    navigate(`/club/${userInfo.club}/scheduledtask/${taskId}`);
  };

  const loadMoreNotifications = () => {
    setVisibleNotifications(prev => prev + 5);
  };

  const formatDateForDisplay = (createdAt) => {
    // Use formatDate to avoid timezone issues
    return formatDate(createdAt) || "Invalid Date";
  };

  return (
    <div className={styles.staffMemberScreen}>
      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>Loading...</p>
        </div>
      ) : error ? (
        <div className={styles.errorContainer}>
          <p>Error: {error}</p>
        </div>
      ) : (
        staffMember && (
          <div className={styles.staffMemberContent}>
            {/* Hero Section */}
            <div className={styles.staffMemberHero}>
              <div className={styles.heroContent}>
                <h1 className={styles.heroTitle}>
                  {staffMember.name} {staffMember.surname}
                </h1>
                <div className={styles.heroSubtitle}>
                  {t(staffMember.position)} • {staffMember.education}
                </div>
              </div>
              <div className={styles.heroBackground}></div>
            </div>

            {/* Staff Member Information Cards */}
            <div className={styles.staffMemberInfoSection}>
              <h2 className={styles.sectionTitle}>
                {t("Staff Member Information").toUpperCase()}
              </h2>
              
              <div className={styles.infoGrid}>
                {/* Personal Info Card */}
                <div className={styles.infoCard}>
                  <h3 className={styles.cardTitle}>👤 Personal Information</h3>
                  <div className={styles.cardContent}>
                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>{t("Name")}:</span>
                      <span className={styles.infoValue}>{staffMember.name}</span>
                    </div>
                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>{t("Surname")}:</span>
                      <span className={styles.infoValue}>{staffMember.surname}</span>
                    </div>
                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>{t("Email")}:</span>
                      <span className={styles.infoValue}>{staffMember.email}</span>
                    </div>
                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>{t("Birthdate")}:</span>
                      <span className={styles.infoValue}>
                        {staffMember.birthdate && staffMember.birthdate !== "not filled" 
                          ? formatBirth(staffMember.birthdate) 
                          : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Professional Info Card */}
                <div className={styles.infoCard}>
                  <h3 className={styles.cardTitle}>💼 Professional Information</h3>
                  <div className={styles.cardContent}>
                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>{t("Position")}:</span>
                      <span className={styles.infoValue}>{t(staffMember.position)}</span>
                    </div>
                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>{t("Education")}:</span>
                      <span className={styles.infoValue}>{staffMember.education}</span>
                    </div>
                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>{t("Nationality")}:</span>
                      <span className={styles.infoValue}>{staffMember.passportNationality}</span>
                    </div>
                  </div>
                </div>

                {/* Passport Info Card */}
                <div className={styles.infoCard}>
                  <h3 className={styles.cardTitle}>📄 Passport Information</h3>
                  <div className={styles.cardContent}>
                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>{t("Passport expiration date")}:</span>
                      <span className={styles.infoValue}>
                        {staffMember.passportExpirationDate && staffMember.passportExpirationDate !== "not filled"
                          ? formatBirth(staffMember.passportExpirationDate)
                          : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Notifications and Tasks Wrapper */}
            <div className={styles.notificationsAndTasksWrapper}>
              {/* Notifications Section */}
              <div className={styles.notificationsSection}>
                <h3 className={styles.sectionTitle}>
                  🔔 {t("Notifications")}
                </h3>
                <div className={styles.notificationsContainer}>
                  {staffMember.notifications && staffMember.notifications.notifications && staffMember.notifications.notifications.length > 0 ? (
                    <>
                      {staffMember.notifications.notifications.slice(0, visibleNotifications).map((notification, index) => (
                        <div key={index} style={{ 
                          maxWidth: window.innerWidth <= 768 ? '100%' : '320px', 
                          margin: window.innerWidth <= 768 ? '8px 0' : '12px auto',
                          width: '100%'
                        }}>
                          <NotificationMessage
                            assigned={notification.send_to?.name || staffMember.name}
                            time={formatDateForDisplay(notification.createdAt)}
                            notification={notification.text}
                          />
                        </div>
                      ))}
                      
                      {/* Load more dugme */}
                      {visibleNotifications < staffMember.notifications.notifications.length && (
                        <div className={styles.loadMoreContainer}>
                          <Button
                            onClick={loadMoreNotifications}
                            style={{
                              backgroundColor: colors.primaryColor,
                              color: "white",
                              border: "none",
                              padding: "10px 20px",
                              cursor: "pointer",
                              marginTop: "15px"
                            }}
                          >
                            {t("Read More")}
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <p style={{ color: '#ffffff', textAlign: 'center', fontStyle: 'italic' }}>
                      {t("No Notifications Available")}
                    </p>
                  )}
                </div>
              </div>

              {/* Assigned Tasks Section */}
              <div className={styles.tasksSection}>
                <h3 className={styles.sectionTitle}>
                  📋 {t("Assigned Tasks")}
                </h3>
                <div className={styles.tasksContainer}>
                  {staffMember.assignedTasks && staffMember.assignedTasks.length > 0 ? (
                    staffMember.assignedTasks.map((assignedTask) => {
                      const taskName = assignedTask.name || "Unnamed Task";
                      const taskDescription = assignedTask.description || "No description available";
                      const taskLocation = assignedTask.location || "No location specified";
                      const taskTime = assignedTask.startTime ? formatDateForDisplay(assignedTask.startTime) : "No time specified";

                      return (
                        <TaskOverview
                          key={assignedTask._id}
                          task={taskName}
                          description={taskDescription}
                          time={taskTime}
                          location={taskLocation}
                          scheduledTaskId={assignedTask._id}
                          onTaskClick={handleTaskClick}
                        />
                      );
                    })
                  ) : (
                    <p style={{ color: '#ffffff', textAlign: 'center', fontStyle: 'italic' }}>
                      {t("No Assigned Tasks Available")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default StaffMemberScreen;
